/**
 * Checkout Service for Hotel Portal
 * Handles checkout operations: list stays, validate, confirm, sync status
 * Requirements: 1.1, 1.3, 1.4, 1.5, 2.1, 6.1, 7.1, 7.6, 12.7
 */

import http, { httpClient } from '../utils/httpClient';
import { apiErrorMessage } from '../utils/apiError';
import {
  ActiveStay,
  CheckoutDetail,
  ValidationResult,
  CheckoutRecord,
  SyncStatus,
  StayFilter,
  InvoiceResponse,
  ConfirmCheckoutData,
  CheckoutConfirmResponse,
  CheckoutError,
} from '../types/checkout';

const BASE = '/api/v1/hotel/checkout';

/**
 * Handle API response with error extraction
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    const error: CheckoutError = {
      code: body.code || `HTTP_${response.status}`,
      message: apiErrorMessage(body, `HTTP ${response.status}`),
      details: body.details,
    };
    throw error;
  }
  return response.json();
}

/**
 * Generate idempotency key for checkout confirmation
 * Format: UUID v4 to ensure uniqueness
 */
export function generateIdempotencyKey(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get list of active stays with optional filters
 * Requirement 1.1, 1.3, 1.4, 1.5
 */
export async function getActiveStays(filter?: StayFilter): Promise<ActiveStay[]> {
  const params = new URLSearchParams();
  if (filter?.status) params.set('status', filter.status);
  if (filter?.search) params.set('search', filter.search);
  if (filter?.sortBy) params.set('sort_by', filter.sortBy);
  if (filter?.sortOrder) params.set('sort_order', filter.sortOrder);
  
  const qs = params.toString();
  const res = await http.get(`${BASE}/stays${qs ? `?${qs}` : ''}`);
  const data = await handleResponse<any>(res);
  
  // API may return { stays: [...], count } wrapper, transform snake_case to camelCase
  const stays = Array.isArray(data) ? data : (data.stays || []);
  return stays.map(transformKeys) as ActiveStay[];
}

/**
 * Transform snake_case keys to camelCase
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Recursively transform object keys from snake_case to camelCase
 */
function transformKeys(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(transformKeys);
  if (typeof obj !== 'object') return obj;
  
  const transformed: any = {};
  for (const key of Object.keys(obj)) {
    const camelKey = snakeToCamel(key);
    transformed[camelKey] = transformKeys(obj[key]);
  }
  return transformed;
}

/**
 * Get checkout detail for a specific stay
 * Requirement 2.1
 */
export async function getCheckoutDetail(stayId: string): Promise<CheckoutDetail> {
  const res = await http.get(`${BASE}/${stayId}`);
  const rawData = await handleResponse<any>(res);
  // API returns { data: {...} } wrapper, transform snake_case to camelCase
  const data = rawData.data || rawData;
  return transformKeys(data) as CheckoutDetail;
}

/**
 * Upload invoice for a stay
 * Requirement 6.1
 */
export async function uploadInvoice(stayId: string, file: File): Promise<InvoiceResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await httpClient(`${BASE}/${stayId}/invoice`, {
    method: 'POST',
    body: formData,
    headers,
  });
  const rawData = await handleResponse<any>(res);
  // API returns { data: { file_url, file_name, uploaded_at }, message: "..." }
  const invoiceData = rawData.data || rawData;
  return transformKeys(invoiceData) as InvoiceResponse;
}

/**
 * Validate checkout before confirmation
 * Requirement 7.1
 */
export async function validateCheckout(stayId: string): Promise<ValidationResult> {
  const res = await http.post(`${BASE}/${stayId}/validate`);
  const rawData = await handleResponse<any>(res);
  // API returns { data: {...} } wrapper, transform snake_case to camelCase
  const data = rawData.data || rawData;
  return transformKeys(data) as ValidationResult;
}

/**
 * Confirm checkout (close the stay)
 * Requirement 7.6
 */
export async function confirmCheckout(
  stayId: string,
  data: ConfirmCheckoutData
): Promise<CheckoutConfirmResponse> {
  const res = await http.post(`${BASE}/${stayId}/confirm`, data);
  const rawData = await handleResponse<any>(res);
  // API returns { data: CheckoutRecordDTO, message: "..." }
  // Transform and wrap into the expected CheckoutConfirmResponse shape
  const checkoutRecord = transformKeys(rawData.data || rawData);
  return {
    checkout: checkoutRecord,
    syncStatus: (rawData.sync_status ? transformKeys(rawData.sync_status) : undefined) as any,
  } as CheckoutConfirmResponse;
}

/**
 * Get sync status for a completed checkout
 * Requirement 12.7
 */
export async function getSyncStatus(checkoutId: string): Promise<SyncStatus> {
  const res = await http.get(`${BASE}/${checkoutId}/status`);
  return handleResponse<SyncStatus>(res);
}

/**
 * Retry sync for failed CNS or ONT transmission
 * Requirement 12.7
 */
export async function retrySync(
  checkoutId: string,
  target: 'cns' | 'ont'
): Promise<void> {
  const res = await http.post(`${BASE}/${checkoutId}/retry-sync`, { target });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(apiErrorMessage(body, `HTTP ${res.status}`));
  }
}

/**
 * Download checkout PDF summary
 * Requirement 12.7
 */
export async function downloadCheckoutPDF(checkoutId: string): Promise<Blob> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await httpClient(`${BASE}/${checkoutId}/pdf`, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(apiErrorMessage(body, `HTTP ${res.status}`));
  }

  return res.blob();
}

/**
 * Update departure info (date, mode, invoice amount)
 */
export async function updateDeparture(
  stayId: string,
  data: { checkoutAt: string; departureMode?: string; invoiceAmount?: number }
): Promise<CheckoutDetail> {
  const res = await http.patch(`${BASE}/${stayId}/departure`, {
    checkout_at: data.checkoutAt,
    departure_mode: data.departureMode || '',
    invoice_amount: data.invoiceAmount,
  });
  const rawData = await handleResponse<any>(res);
  const detail = rawData.data || rawData;
  return transformKeys(detail) as CheckoutDetail;
}

/**
 * Checkout service object with all methods
 */
export const checkoutService = {
  getActiveStays,
  getCheckoutDetail,
  uploadInvoice,
  updateDeparture,
  validateCheckout,
  confirmCheckout,
  getSyncStatus,
  retrySync,
  downloadCheckoutPDF,
  generateIdempotencyKey,
};

export default checkoutService;
